import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';
import {Check, CircleDot, ExternalLink} from 'lucide-react';
import {apiErrorMessage} from '../api/abfApi';
import {StatusChip} from '../components/StatusChip';
import {DateField} from '../../../components/shared/DateField';
import {Button, INK} from '../onboarding/review/reviewStyles';
import {DecisionBar, Journey, Layout, SmallButton, TaskButton, TaskRail, Work} from '../onboarding/review/contractReviewStyles';
import {ffApi, type Agreement, type ProgramDetail} from './forwardFlowApi';
import {ForwardFlowAmendmentImpact} from './ForwardFlowAmendmentImpact';
import {AmountInput, amountOf, ConfirmAction, Fields, GatedAction, Grid, Notice, Row, Scroller, Section, useCommand, useFeedback, useViewer} from './ForwardFlowShared';
import {day, moment, money, optionWords, TERM_OPTIONS, termLabel, word} from './forwardFlowWords';

type Terms = Record<string, any>;
type Candidate = {value: string; sourceExcerpt: string; authority: string};

const LaneChoice = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; margin: 0 0 16px;
  button { display: flex; flex-direction: column; gap: 6px; text-align: left; padding: 18px 20px; min-height: 120px;
    border: 1px solid ${INK.lineStrong}; border-radius: 12px; background: ${INK.raised}; color: ${INK.body}; cursor: pointer; font: inherit; }
  button:hover, button:focus-visible { border-color: ${INK.accent}; }
  button[aria-pressed='true'] { border-color: ${INK.accent}; background: #19334a; }
  strong { font-size: 15px; color: ${INK.strong}; }
  span { color: ${INK.muted}; font-size: 13px; line-height: 1.5; }
  @media (max-width: 740px) { grid-template-columns: 1fr; }
`;
const Group = styled.div`
  border-top: 1px solid ${INK.line}; margin-top: 18px; padding-top: 16px;
  h3 { margin: 0 0 3px; } > p { margin: 0 0 4px; font-size: 12px; }
`;
const Permits = styled.div`
  display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 10px; margin: 12px 0;
  label { display: flex; align-items: flex-start; gap: 10px; padding: 11px 13px; border: 1px solid ${INK.line}; border-radius: 9px;
    background: ${INK.raised}; font-size: 13px; font-weight: 400; color: ${INK.body}; line-height: 1.45; }
  input { width: 16px; height: 16px; margin: 2px 0 0; accent-color: ${INK.accent}; }
  @media (max-width: 780px) { grid-template-columns: 1fr; }
`;

/** The terms every program of this kind runs on; shown, not asked. */
const FIXED: Terms = {
  profileCode: 'USD_FIXED_INSTALLMENT_WHOLE_V1', currency: 'USD', assetType: 'INSTALLMENT_LOAN', rateType: 'FIXED',
  paymentFrequency: 'MONTHLY', ownershipPct: 100, purchaseCadence: 'WEEKLY', servicingCadence: 'MONTHLY',
  holidayCalendar: 'LSTA_US', priceMode: 'FIXED_PER_100_UPB', timezone: 'America/New_York',
  legalTransferTrigger: 'ASSIGNMENT_CONFIRMED',
};
const DEFAULTS: Terms = {
  ...FIXED, selectionMode: 'PARTIAL', buyerObligation: 'DISCRETIONARY', sellerObligation: 'BEST_EFFORTS',
  purchaseWeekday: 'MONDAY', dayCountConvention: 'ACT/365F', internalProfileReviewed: false,
  allowBuyerFee: false, buyerFeeTreatment: 'CAPITALIZE', allowPriceAdjustment: false,
  priceAdjustmentTreatment: 'PRINCIPAL_COST', allowHoldback: false, holdbackDueDays: 30, allowPriorCredit: false,
};
const GROUPS: Array<{title: string; hint: string; fields: string[]}> = [
  {title: 'Parties and evidence', hint: 'Who sells, who services, and the documents the terms rest on.',
    fields: ['sellerName', 'servicerName', 'originatorNamespace', 'legalAssessmentRef', 'settlementInstructionsRef']},
  {title: 'What may be bought, and at what price', hint: 'These terms decide every offer: the limits, the price and the purchase day.',
    fields: ['effectiveFrom', 'pricePer100', 'cumulativeLimitUpb', 'monthlyLimitUpb', 'purchaseWeekday', 'selectionMode', 'buyerObligation', 'sellerObligation']},
  {title: 'Eligible loans and servicing', hint: 'Each offered loan is checked against these. Servicing dates continue after a purchase.',
    fields: ['underwritingPolicyVersion', 'minCreditScore', 'maxStateConcentrationPct', 'dayCountConvention', 'servicingDueDay']},
];
const REQUIRED = ['sellerName', 'servicerName', 'originatorNamespace', 'legalAssessmentRef', 'settlementInstructionsRef',
  'effectiveFrom', 'pricePer100', 'cumulativeLimitUpb', 'monthlyLimitUpb', 'underwritingPolicyVersion', 'minCreditScore',
  'maxStateConcentrationPct', 'servicingDueDay'];
const AMOUNTS = new Set(['cumulativeLimitUpb', 'monthlyLimitUpb']);
const NUMBERS = new Set(['pricePer100', 'minCreditScore', 'maxStateConcentrationPct', 'servicingDueDay', 'holdbackDueDays']);
const HINTS: Record<string, string> = {
  originatorNamespace: "A short code for the seller's loan ids, e.g. GRANITE. It keeps one seller's ids apart from another's.",
  pricePer100: 'e.g. 99.50 means $99.50 paid for every $100 of balance.',
  servicingDueDay: 'Day of the month, 1 to 28.', maxStateConcentrationPct: 'Percent of an offer, 0 to 100.',
  minCreditScore: '0 to 900.', holdbackDueDays: 'Calendar days after transfer.',
};

const parse = (value: unknown): any => {
  if (typeof value === 'string') { try { return JSON.parse(value); } catch { return {}; } }
  if (value && typeof value === 'object' && 'value' in (value as any) && typeof (value as any).value === 'string')
    return parse((value as any).value);
  return value ?? {};
};

function display(key: string, value: unknown): string {
  if (value === null || value === undefined || value === '') return 'Not recorded';
  if (typeof value === 'boolean') return value ? 'Permitted' : 'Not permitted';
  if (key === 'effectiveFrom') return day(String(value));
  if (AMOUNTS.has(key)) return money(amountOf(String(value)), 0);
  if (key === 'pricePer100') return `${money(value)} per $100 of balance`;
  if (key === 'maxStateConcentrationPct') return `${value}%`;
  if (key === 'servicingDueDay') return `Day ${value} of each month`;
  if (key === 'holdbackDueDays') return `${value} days after transfer`;
  if (TERM_OPTIONS[key]) return optionWords(key, value);
  return String(value);
}

/** What is missing or out of range, in the reader's words. */
function gaps(terms: Terms): string[] {
  const out: string[] = [];
  for (const key of REQUIRED) if (terms[key] === undefined || terms[key] === null || String(terms[key]).trim() === '') out.push(`${termLabel(key)} is missing`);
  for (const key of Array.from(AMOUNTS)) if (String(terms[key] ?? '').trim() !== '' && !((amountOf(String(terms[key])) ?? 0) > 0)) out.push(`${termLabel(key)} is not an amount above zero`);
  const n = (key: string) => Number(terms[key]);
  if (String(terms.pricePer100 ?? '') !== '' && !(n('pricePer100') > 0)) out.push('Price must be above zero');
  if (String(terms.minCreditScore ?? '') !== '' && !(Number.isInteger(n('minCreditScore')) && n('minCreditScore') >= 0 && n('minCreditScore') <= 900)) out.push('Minimum credit score must be a whole number from 0 to 900');
  if (String(terms.maxStateConcentrationPct ?? '') !== '' && !(n('maxStateConcentrationPct') >= 0 && n('maxStateConcentrationPct') <= 100)) out.push('Maximum share in one state must be from 0 to 100');
  if (String(terms.servicingDueDay ?? '') !== '' && !(Number.isInteger(n('servicingDueDay')) && n('servicingDueDay') >= 1 && n('servicingDueDay') <= 28)) out.push('Servicer report due day must be from 1 to 28');
  if (terms.allowHoldback && !(n('holdbackDueDays') > 0)) out.push('Holdback needs the number of days after which it is due');
  const cumulative = amountOf(String(terms.cumulativeLimitUpb ?? '')), monthly = amountOf(String(terms.monthlyLimitUpb ?? ''));
  if (cumulative && monthly && monthly > cumulative) out.push('The monthly limit is larger than the total limit');
  if (!terms.internalProfileReviewed) out.push('Confirm that you reviewed the operating assumptions');
  return out;
}

function forServer(terms: Terms): Terms {
  const out: Terms = {...terms, ...FIXED};
  for (const key of Object.keys(out)) if (typeof out[key] === 'string') out[key] = out[key].trim();
  for (const key of Array.from(AMOUNTS)) out[key] = String(amountOf(String(out[key] ?? '')) ?? '');
  return out;
}

/**
 * The purchase agreement, version by version. One screen for both ways in (the PDF or
 * direct entry) and one next action in the bar: send, verify or activate.
 */
export function ForwardFlowAgreementWorkspace({programId, detail, requestedVersion, onChanged}: {
  programId: number; detail: ProgramDetail; requestedVersion?: number | null; onChanged: () => Promise<unknown> | void;
}) {
  const viewer = useViewer();
  const notify = useFeedback();
  const {busy, run} = useCommand(onChanged);
  const [agreement, setAgreement] = useState<Agreement | null>(null);
  const [terms, setTerms] = useState<Terms>(DEFAULTS);
  const [saved, setSaved] = useState('');
  const [candidates, setCandidates] = useState<Record<string, Candidate>>({});
  const [starting, setStarting] = useState(false);
  const [lane, setLane] = useState<'document' | 'structured' | null>(null);
  const [uploading, setUploading] = useState(false);
  const pdf = useRef<HTMLInputElement>(null);

  const versions = detail.versions;
  const active = versions.find(v => v.id === detail.active_agreement_id);
  const pending = versions.find(v => v.id !== detail.active_agreement_id && ['DRAFT', 'IN_REVIEW', 'VERIFIED', 'SCHEDULED'].includes(v.status));

  const open = useCallback(async (id: number) => {
    try {
      const a = await ffApi.agreement(programId, id);
      const loaded = {...DEFAULTS, ...parse(a.terms)};
      for (const key of Array.from(AMOUNTS)) if (loaded[key] !== undefined && loaded[key] !== '') loaded[key] = Number(loaded[key]).toLocaleString('en-US');
      setAgreement(a); setTerms(loaded); setSaved(JSON.stringify(loaded)); setStarting(false); setLane(null);
      setCandidates(a.document_id ? parse((await ffApi.documentEvidence(programId, a.document_id)).extracted_candidates) : {});
    } catch (e) { notify({tone: 'error', message: apiErrorMessage(e, 'The agreement version could not be opened.')}); }
  }, [programId, notify]);

  // Open the version that needs work: the one asked for, else the one in progress, else the one in force.
  const target = requestedVersion && versions.some(v => v.id === requestedVersion) ? requestedVersion : (pending ?? active ?? versions[0])?.id;
  const statusKey = versions.map(v => `${v.id}:${v.status}:${v.row_version}`).join(',');
  useEffect(() => {
    if (!target) { setAgreement(null); return; }
    if (agreement && agreement.id !== target && !requestedVersion && versions.some(v => v.id === agreement.id)) {
      void open(agreement.id); // keep the reader's own selection, refreshed
      return;
    }
    void open(target);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [target, statusKey]);

  const draft = agreement?.status === 'DRAFT';
  const dirty = draft && JSON.stringify(terms) !== saved;
  const missing = useMemo(() => draft ? gaps(terms) : [], [draft, terms]);
  const ownWork = !!viewer.username && [agreement?.created_by, agreement?.reviewed_by]
    .some(name => name && name.toLowerCase() === viewer.username!.toLowerCase());
  const set = (key: string, value: unknown) => setTerms(t => ({...t, [key]: value}));

  const save = () => run(async () => {
    const r = await ffApi.saveTerms(programId, agreement!.id, agreement!.row_version, forServer(terms));
    await open(agreement!.id);
    return r;
  }, 'Draft saved.', 'The draft could not be saved.');

  const send = () => run(async () => {
    const savedTerms = await ffApi.saveTerms(programId, agreement!.id, agreement!.row_version, forServer(terms));
    const rowVersion = Number((savedTerms as any).rowVersion ?? agreement!.row_version + 1);
    const r = await ffApi.act(programId, agreement!.id, 'submit', rowVersion);
    await open(agreement!.id);
    return r;
  }, 'Terms saved and sent to a checker.', 'The terms could not be sent.');

  const decide = (action: 'verify' | 'activate', done: string) => run(async () => {
    const r = await ffApi.act(programId, agreement!.id, action, agreement!.row_version);
    await open(agreement!.id);
    return r;
  }, done);

  const start = (sourceLane: 'STRUCTURED' | 'DOCUMENT', documentId?: number) => run(async () => {
    const v = await ffApi.createAgreement(programId, sourceLane, documentId);
    await onChanged();
    await open(v.agreementId);
    return v;
  }, sourceLane === 'DOCUMENT' ? 'Agreement stored. Review the terms found in it.' : 'Draft started. Enter the terms.');

  const upload = async (file: File | null) => {
    if (!file) return;
    setUploading(true);
    try {
      const stored = await ffApi.uploadAgreement(programId, file);
      await start('DOCUMENT', stored.documentId);
    } catch (e) { notify({tone: 'error', message: apiErrorMessage(e, `${file.name} could not be stored. Use a readable PDF up to 20 MB.`)}); }
    finally { setUploading(false); if (pdf.current) pdf.current.value = ''; }
  };

  const useAll = () => {
    const next = {...terms};
    for (const [key, c] of Object.entries(candidates)) next[key] = c.value;
    setTerms(next);
  };

  const field = (key: string) => {
    const label = termLabel(key);
    if (key === 'effectiveFrom') return <label key={key}>{label}
      <DateField value={terms.effectiveFrom ?? ''} onChange={v => set(key, v)} today={detail.businessDate} aria-label={label} />
      <small>On or before today puts the terms in force at once; a later date schedules them.</small></label>;
    if (TERM_OPTIONS[key]) return <label key={key}>{label}
      <select value={terms[key] ?? ''} onChange={e => set(key, e.target.value)}>
        {TERM_OPTIONS[key].map(([code, words]) => <option key={code} value={code}>{words}</option>)}</select></label>;
    if (AMOUNTS.has(key)) return <label key={key}>{label}
      <AmountInput value={String(terms[key] ?? '')} onChange={v => set(key, v)} placeholder="e.g. 25MM" aria-label={label} /></label>;
    return <label key={key}>{label}
      <input inputMode={NUMBERS.has(key) ? 'decimal' : undefined} value={terms[key] ?? ''} onChange={e => set(key, e.target.value)} />
      {HINTS[key] && <small>{HINTS[key]}</small>}</label>;
  };

  const showStart = versions.length === 0 || starting;

  return <>
    <Journey aria-label="How an agreement version comes into force">
      <li data-current={(!!agreement && agreement.status === 'DRAFT') || versions.length === 0}>
        <span className={`step-icon${agreement && agreement.status !== 'DRAFT' ? ' done' : ''}`}>{agreement && agreement.status !== 'DRAFT' ? <Check size={15} /> : <CircleDot size={15} />}</span>
        <div><strong>1. Terms entered</strong><small>From the agreement PDF or typed in</small></div></li>
      <li data-current={agreement?.status === 'IN_REVIEW'}>
        <span className={`step-icon${agreement && ['VERIFIED', 'SCHEDULED', 'ACTIVE', 'SUPERSEDED'].includes(agreement.status) ? ' done' : ''}`}>
          {agreement && ['VERIFIED', 'SCHEDULED', 'ACTIVE', 'SUPERSEDED'].includes(agreement.status) ? <Check size={15} /> : <CircleDot size={15} />}</span>
        <div><strong>2. Verified by a checker</strong><small>Someone who did not enter the terms</small></div></li>
      <li data-current={agreement?.status === 'VERIFIED' || agreement?.status === 'SCHEDULED'}>
        <span className={`step-icon${agreement && ['ACTIVE', 'SUPERSEDED'].includes(agreement.status) ? ' done' : ''}`}>
          {agreement && ['ACTIVE', 'SUPERSEDED'].includes(agreement.status) ? <Check size={15} /> : <CircleDot size={15} />}</span>
        <div><strong>3. In force</strong><small>{active ? `Version ${active.version_number} since ${day(active.effective_from)}` : 'From its effective date'}</small></div></li>
    </Journey>

    {showStart && <Section>
      <header><div><h2>{versions.length ? 'Start a new agreement version' : 'Enter the purchase agreement'}</h2>
        <p>{versions.length ? 'The version in force stays in force until a checker activates the new one.'
          : 'Nothing can be purchased until an agreement is in force. Start from the signed PDF, or type the terms.'}</p></div>
        {versions.length > 0 && <SmallButton type="button" onClick={() => { setStarting(false); setLane(null); }}>Cancel</SmallButton>}</header>
      {!viewer.maker ? <Notice>An operations or portfolio user enters the agreement. You can review it once it is sent to a checker.</Notice> : <>
        <LaneChoice>
          <button type="button" aria-pressed={lane === 'document'} onClick={() => setLane('document')}>
            <strong>Start from the agreement PDF</strong>
            <span>The PDF is kept as evidence and its terms are read out for you to check. Nothing it suggests counts until you accept it.</span></button>
          <button type="button" aria-pressed={lane === 'structured'} onClick={() => setLane('structured')}>
            <strong>Type the terms</strong>
            <span>Enter the terms directly. The same checker verification and activation apply.</span></button>
        </LaneChoice>
        <input ref={pdf} type="file" hidden accept="application/pdf,.pdf" aria-label="Agreement PDF" onChange={e => void upload(e.target.files?.[0] ?? null)} />
        {lane === 'document' && <Button $variant="primary" type="button" disabled={busy || uploading} onClick={() => pdf.current?.click()}>
          {uploading ? 'Reading the agreement…' : 'Choose the agreement PDF'}</Button>}
        {lane === 'structured' && <Button $variant="primary" type="button" disabled={busy} onClick={() => void start('STRUCTURED')}>Start entering terms</Button>}
      </>}
    </Section>}

    {versions.length > 0 && <Layout>
      <TaskRail aria-label="Agreement versions">
        <div className="rail-heading"><h2>Versions</h2><p>Every version is kept with who entered, verified and activated it.</p></div>
        <div className="tasks">{versions.map(v => <TaskButton key={v.id} type="button" $active={agreement?.id === v.id} onClick={() => void open(v.id)}>
          <span className="category">{v.source_lane === 'DOCUMENT' ? 'From the agreement PDF' : 'Typed in'}</span>
          <strong>Version {v.version_number}</strong>
          <span className="reason"><StatusChip tone={word('agreement', v.status).tone} label={word('agreement', v.status).label} /></span>
          <span className="owner">Effective {v.effective_from ? day(v.effective_from) : 'not set'} · entered {moment(v.created_at)}</span>
        </TaskButton>)}</div>
        {viewer.maker && !pending && !starting && !['EXPIRED', 'TERMINATED', 'CLOSED'].includes(detail.status) && <div className="rail-footer">
          <SmallButton type="button" onClick={() => { setStarting(true); setLane(null); }}>+ New version</SmallButton>
          <p>Use a new version to amend the terms.</p></div>}
      </TaskRail>

      <Work aria-label="Selected agreement version">
        {agreement && <Section>
          <header><div><h2>Version {agreement.version_number}</h2>
            <p>Entered by {agreement.created_by ?? 'unknown'} · {moment(agreement.created_at)}
              {agreement.verified_by ? ` · verified by ${agreement.verified_by}` : ''}{agreement.activated_by ? ` · activated by ${agreement.activated_by}` : ''}</p></div>
            <Row><StatusChip tone={word('agreement', agreement.status).tone} label={word('agreement', agreement.status).label} size="md" />
              {agreement.document_id && <a href={`/api/credit/abf/forward-flow/programs/${programId}/documents/${agreement.document_id}`}
                target="_blank" rel="noreferrer" style={{color: INK.accentSoft, fontSize: 13, display: 'inline-flex', gap: 5, alignItems: 'center'}}>
                Open the agreement PDF <ExternalLink size={13} /></a>}</Row></header>

          <Notice>This program buys whole US-dollar, fixed-rate instalment loans with monthly payments, offered weekly and serviced monthly,
            at a fixed price per $100 of balance. Ownership passes when the assignment is confirmed.</Notice>

          {draft && Object.keys(candidates).length > 0 && <Group style={{borderTop: 0, marginTop: 0, paddingTop: 0}}>
            <Row style={{justifyContent: 'space-between', alignItems: 'center'}}>
              <div><h3>Found in the agreement PDF</h3><p>Check each against the document. A value counts only once you use it and send the terms.</p></div>
              <SmallButton type="button" onClick={useAll}>Use all {Object.keys(candidates).length} values</SmallButton></Row>
            <Scroller><Grid><thead><tr><th>Term</th><th>Value found</th><th>Where it was found</th><th></th></tr></thead>
              <tbody>{Object.entries(candidates).map(([key, c]) => {
                const used = String(terms[key] ?? '') === c.value;
                return <tr key={key}><td>{termLabel(key)}</td><td>{c.value}</td><td className="muted">“{c.sourceExcerpt}”</td>
                  <td>{used ? <StatusChip tone="done" label="In use" /> : <SmallButton type="button" onClick={() => set(key, c.value)}>Use this value</SmallButton>}</td></tr>;
              })}</tbody></Grid></Scroller>
          </Group>}

          {draft ? <>
            {GROUPS.map(g => <Group key={g.title}><h3>{g.title}</h3><p>{g.hint}</p><Fields>{g.fields.map(field)}</Fields></Group>)}
            <Group><h3>Optional price components</h3><p>Tick only what the agreement allows. Each one ticked can then be used on an offer.</p>
              <Permits>
                {(['allowBuyerFee', 'allowPriceAdjustment', 'allowHoldback', 'allowPriorCredit'] as const).map(key => <label key={key}>
                  <input type="checkbox" checked={!!terms[key]} onChange={e => set(key, e.target.checked)} />
                  <span>The agreement allows a {termLabel(key).toLowerCase()}</span></label>)}
              </Permits>
              {(terms.allowBuyerFee || terms.allowPriceAdjustment || terms.allowHoldback) && <Fields>
                {terms.allowBuyerFee && field('buyerFeeTreatment')}
                {terms.allowPriceAdjustment && field('priceAdjustmentTreatment')}
                {terms.allowHoldback && field('holdbackDueDays')}
              </Fields>}
            </Group>
            <Group><Permits style={{gridTemplateColumns: '1fr'}}><label>
              <input type="checkbox" checked={!!terms.internalProfileReviewed} onChange={e => set('internalProfileReviewed', e.target.checked)} />
              <span>I reviewed the operating assumptions above (loan type, weekly purchases, monthly servicing, fixed price) against this agreement.</span>
            </label></Permits></Group>
          </> : <>
            {[...GROUPS, {title: 'Optional price components', hint: '', fields: ['allowBuyerFee', 'allowPriceAdjustment', 'allowHoldback', 'allowPriorCredit',
                ...(terms.allowBuyerFee ? ['buyerFeeTreatment'] : []), ...(terms.allowPriceAdjustment ? ['priceAdjustmentTreatment'] : []),
                ...(terms.allowHoldback ? ['holdbackDueDays'] : [])]}].map(g => <Group key={g.title}><h3>{g.title}</h3>
              <Grid><tbody>{g.fields.map(key => <tr key={key}><td style={{width: '40%'}} className="muted">{termLabel(key)}</td>
                <td><strong>{display(key, terms[key])}</strong></td></tr>)}</tbody></Grid></Group>)}
          </>}

          {detail.agreement && agreement.id !== detail.agreement.id && agreement.status !== 'SUPERSEDED' &&
            <ForwardFlowAmendmentImpact programId={programId} versionId={agreement.id} />}
        </Section>}

        {agreement && <DecisionBar aria-label="Next action on this version">
          {draft && <>
            <div><strong>{missing.length ? `${missing.length} thing${missing.length === 1 ? '' : 's'} to complete before sending` : 'Send the terms to a checker'}</strong>
              <span>{missing.length ? missing.slice(0, 3).join(' · ') + (missing.length > 3 ? ` · and ${missing.length - 3} more` : '')
                : 'Your entries are saved first. A different person verifies them; nothing is in force until activation.'}</span></div>
            <Row>
              <Button type="button" disabled={busy || !dirty || !viewer.maker} onClick={() => void save()}>{dirty ? 'Save draft' : 'Saved'}</Button>
              <GatedAction variant="primary" busy={busy} disabled={missing.length > 0}
                reason={!viewer.maker ? 'An operations or portfolio user sends the terms.' : null} onClick={() => void send()}>Send to a checker</GatedAction>
            </Row></>}
          {agreement.status === 'IN_REVIEW' && <>
            <div><strong>Verify the terms</strong><span>Compare every term with the agreement. Verifying records that you checked them.</span></div>
            <GatedAction variant="primary" busy={busy}
              reason={!viewer.checker ? 'A checker (risk or compliance) verifies the terms.' : ownWork ? 'You entered these terms; a different checker verifies them.' : null}
              onClick={() => void decide('verify', 'Terms verified. They can now be activated.')}>Verify the terms</GatedAction></>}
          {agreement.status === 'VERIFIED' && <>
            <div><strong>Activate version {agreement.version_number}</strong>
              <span>{terms.effectiveFrom && terms.effectiveFrom > detail.businessDate ? `It takes effect on ${day(terms.effectiveFrom)}; until then the current version stays in force.`
                : 'It takes effect today and replaces the version in force.'}</span></div>
            <ConfirmAction label="Activate" title={`Activate version ${agreement.version_number}?`} busy={busy}
              body={`Offers received from ${day(terms.effectiveFrom)} are decided on these terms.\nPurchases already approved keep the version they were approved under. No loan is bought and no cash moves.`}
              disabledReason={!viewer.checker ? 'A checker (risk or compliance) activates the agreement.' : ownWork ? 'You entered these terms; a different checker activates them.' : null}
              onConfirm={() => decide('activate', 'Agreement activated.')} /></>}
          {agreement.status === 'SCHEDULED' && <div><strong>Scheduled for {day(agreement.effective_from)}</strong>
            <span>Nothing to do. It comes into force on that date and the current version stays in force until then.</span></div>}
          {agreement.status === 'ACTIVE' && <div><strong>In force since {day(agreement.effective_from)}</strong>
            <span>To change a term, start a new version; this one stays in force until the new one is activated.</span></div>}
          {agreement.status === 'SUPERSEDED' && <div><strong>Replaced by a later version</strong>
            <span>Kept for the record. Purchases approved under it keep its terms.</span></div>}
        </DecisionBar>}
      </Work>
    </Layout>}
  </>;
}
